/**
 * O editor do Modo História.
 *
 * Três níveis, encaixados: a campanha contém capítulos, e cada capítulo contém
 * cenas. A tela segue a mesma hierarquia — escolhe-se a campanha à esquerda, o
 * capítulo no meio, e as cenas dele à direita — porque é assim que a coisa é
 * pensada por quem escreve: primeiro a história, depois o capítulo, depois a
 * fala.
 *
 * A mídia (imagem e vídeo das cutscenes) é enviada uma vez e reaproveitada:
 * quem escreve uma campanha de vinte capítulos não deveria subir o mesmo fundo
 * vinte vezes.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, messageOf } from '../../api/client.js';
import { objectiveLabel, type ChapterRecruit, type RewardItem } from '../../game/story.js';
import type { MapObjectiveData } from '../../render/tilemap.js';
import { MapEditor } from '../MapEditor.js';

interface Campaign {
  readonly id: number;
  readonly slug: string;
  readonly title: string;
  readonly summary: string | null;
  readonly status: string;
  readonly chapters: number;
}

interface Scene {
  readonly id: number;
  readonly moment: 'abertura' | 'vitoria' | 'derrota' | 'intervalo';
  readonly ordinal: number;
  readonly kind: 'dialogo' | 'narracao' | 'imagem' | 'video';
  readonly speaker: string | null;
  readonly portrait: string | null;
  readonly portraitIndex: number;
  readonly body: string | null;
  readonly background: string | null;
  readonly media: { readonly url: string; readonly kind: string } | null;
}

interface Chapter {
  readonly id: number;
  readonly ordinal: number;
  readonly title: string;
  readonly mapId: number;
  readonly objective: MapObjectiveData | null;
  readonly rewardCoins: number;
  readonly rewardItems: readonly RewardItem[];
  readonly unlockCharacters: readonly number[];
  readonly allowedCharacters: readonly number[] | null;
  /** Os inimigos que mudam de lado se falarem com eles. Ver `ChapterRecruit`. */
  readonly recruits: readonly ChapterRecruit[];
  readonly enemyLevel: number;
  readonly difficulty: 'facil' | 'normal' | 'dificil';
  readonly scenes: readonly Scene[];
}

/** O mínimo de uma ficha do acervo para escolher personagem ou item. */
interface Pick {
  readonly id: number;
  readonly name: string;
}

interface MediaAsset {
  readonly id: number;
  readonly kind: string;
  readonly url: string;
  readonly originalName: string;
  readonly bytes: number;
}

interface MapChoice {
  readonly id: number;
  readonly name: string;
  readonly width: number;
  readonly height: number;
}

const MOMENTS = [
  ['abertura', 'Antes da batalha'],
  ['vitoria', 'Depois da vitória'],
  ['derrota', 'Depois da derrota'],
  ['intervalo', 'Intervalo'],
] as const;

const OBJECTIVES = [
  ['', 'O do mapa'],
  ['derrotar_todos', 'Derrotar todos'],
  ['derrotar_comandante', 'Derrotar o comandante'],
  ['sobreviver', 'Sobreviver N turnos'],
  ['tomar_posicao', 'Tomar posição'],
] as const;

/**
 * O que faz de um capítulo um capítulo, e não uma escaramuça: objetivo, quem
 * pode lutar, o nível dos inimigos e o que a primeira vitória entrega.
 *
 * Cada controle grava só o próprio campo — o servidor atualiza em parte, e um
 * ajuste de título não apaga mais as recompensas.
 */
function ChapterRules({
  chapter,
  characters,
  rewardPool,
  onSave,
}: {
  readonly chapter: Chapter;
  readonly characters: readonly Pick[];
  readonly rewardPool: { readonly weapons: readonly Pick[]; readonly items: readonly Pick[] };
  readonly onSave: (patch: Record<string, unknown>, message?: string) => void;
}): JSX.Element {
  const objective = chapter.objective;
  const [newReward, setNewReward] = useState('');
  const [newQty, setNewQty] = useState(1);
  const [newRecruit, setNewRecruit] = useState('');

  const recruits = chapter.recruits ?? [];
  const patchRecruit = (index: number, patch: Partial<ChapterRecruit>): void => {
    onSave({ recruits: recruits.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  };

  const toggle = (list: readonly number[] | null, id: number): number[] => {
    const current = list ?? [];
    return current.includes(id) ? current.filter((v) => v !== id) : [...current, id];
  };

  const addReward = () => {
    const [type, rawId] = newReward.split(':');
    const contentId = Number(rawId);
    if ((type !== 'weapons' && type !== 'items') || !contentId) return;
    const pool = type === 'weapons' ? rewardPool.weapons : rewardPool.items;
    const name = pool.find((p) => p.id === contentId)?.name ?? '';
    onSave(
      { rewardItems: [...chapter.rewardItems, { contentType: type, contentId, quantity: newQty, name }] },
      'Recompensa acrescentada.',
    );
    setNewReward('');
    setNewQty(1);
  };

  return (
    <div className="chapter-rules">
      <label className="form-field">
        <span>Objetivo</span>
        <select
          className="form-select"
          value={objective?.kind ?? ''}
          onChange={(e) => {
            const kind = e.target.value;
            if (!kind) {
              onSave({ objective: null }, 'Objetivo: o do mapa.');
              return;
            }
            onSave(
              { objective: { kind, ...(kind === 'sobreviver' ? { turns: 5 } : {}) } },
              'Objetivo atualizado.',
            );
          }}
        >
          {OBJECTIVES.map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
      </label>

      {objective?.kind === 'sobreviver' && (
        <label className="form-field">
          <span>Turnos a sobreviver</span>
          <input
            type="number"
            className="form-input"
            min={1}
            max={30}
            defaultValue={objective.turns ?? 5}
            onBlur={(e) => onSave(
              { objective: { kind: 'sobreviver', turns: Math.max(1, Number(e.target.value) || 1) } },
              'Turnos atualizados.',
            )}
          />
        </label>
      )}

      {objective?.kind === 'tomar_posicao' && (
        <div className="form-row-2">
          {(['x', 'y'] as const).map((axis) => (
            <label key={axis} className="form-field">
              <span>Casa {axis.toUpperCase()}</span>
              <input
                type="number" className="form-input" min={0}
                defaultValue={objective.at?.[axis] ?? 0}
                onBlur={(e) => onSave({
                  objective: { kind: 'tomar_posicao', at: { ...(objective.at ?? { x: 0, y: 0 }), [axis]: Number(e.target.value) } },
                })}
              />
            </label>
          ))}
        </div>
      )}

      <label className="form-field">
        <span>Nível dos inimigos</span>
        <input
          type="number" className="form-input" min={1} max={20}
          defaultValue={chapter.enemyLevel}
          onBlur={(e) => {
            if (Number(e.target.value) !== chapter.enemyLevel) onSave({ enemyLevel: Number(e.target.value) });
          }}
        />
      </label>

      <fieldset className="story-checks">
        <legend>Quem pode lutar <span className="muted">(nenhum marcado: qualquer um)</span></legend>
        {characters.map((c) => (
          <label key={c.id}>
            <input
              type="checkbox"
              checked={(chapter.allowedCharacters ?? []).includes(c.id)}
              onChange={() => onSave({ allowedCharacters: toggle(chapter.allowedCharacters, c.id) })}
            />
            {c.name}
          </label>
        ))}
      </fieldset>

      <fieldset className="story-checks">
        <legend>Entra no esquadrão ao vencer</legend>
        {characters.map((c) => (
          <label key={c.id}>
            <input
              type="checkbox"
              checked={chapter.unlockCharacters.includes(c.id)}
              onChange={() => onSave({ unlockCharacters: toggle(chapter.unlockCharacters, c.id) })}
            />
            {c.name}
          </label>
        ))}
      </fieldset>

      {/*
        O recrutamento por conversa — o "Talk" dos Fire Emblem.

        A ligação com o tabuleiro é o NOME da unidade inimiga — o que o editor
        de mapas mostra na tropa anotada, ou o nome do bicho no bestiário
        quando o mapa não traz tropa. O personagem do acervo é o que faz o
        recruta durar mais que a batalha; sem ele, a unidade luta ao seu lado
        neste capítulo e não entra no esquadrão depois.
      */}
      <fieldset className="story-checks">
        <legend>
          Entra no esquadrão ao CONVERSAR{' '}
          <span className="muted">(inimigo no mapa que muda de lado)</span>
        </legend>
        {recruits.length === 0 && <span className="muted">nenhum</span>}
        {recruits.map((r, index) => (
          <div key={index} className="story-recruit-row">
            <div className="story-recruit-line">
              <input
                type="text"
                className="form-input"
                placeholder="Nome da unidade inimiga no mapa"
                defaultValue={r.unitName}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && value !== r.unitName) patchRecruit(index, { unitName: value });
                }}
              />
              <button
                type="button" className="scene-delete" title="Tirar este recruta"
                onClick={() => onSave({ recruits: recruits.filter((_, i) => i !== index) })}
              >
                ✕
              </button>
            </div>
            <div className="story-recruit-line">
              <label className="form-field">
                <span>Convencido por</span>
                <select
                  className="form-select"
                  value={r.byName ?? ''}
                  onChange={(e) => patchRecruit(index, { byName: e.target.value })}
                >
                  <option value="">qualquer um do esquadrão</option>
                  {characters.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </label>
              <label className="form-field">
                <span>É o personagem</span>
                <select
                  className="form-select"
                  value={r.characterId ?? 0}
                  onChange={(e) => patchRecruit(index, { characterId: Number(e.target.value) })}
                >
                  <option value={0}>só nesta batalha</option>
                  {characters.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <textarea
              className="form-input"
              rows={2}
              placeholder="O que ele diz ao mudar de lado"
              defaultValue={r.dialogue ?? ''}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (value !== (r.dialogue ?? '')) patchRecruit(index, { dialogue: value });
              }}
            />
          </div>
        ))}
        <div className="story-reward-row">
          <input
            type="text"
            className="form-input"
            placeholder="+ nome do inimigo no mapa…"
            value={newRecruit}
            onChange={(e) => setNewRecruit(e.target.value)}
          />
          <button
            type="button"
            className="importer-secondary"
            disabled={!newRecruit.trim()}
            onClick={() => {
              onSave(
                { recruits: [...recruits, { unitName: newRecruit.trim() }] },
                'Recruta por conversa acrescentado.',
              );
              setNewRecruit('');
            }}
          >
            ok
          </button>
        </div>
      </fieldset>

      <fieldset className="story-checks">
        <legend>Itens da primeira vitória <span className="muted">(inventário da conta)</span></legend>
        {chapter.rewardItems.length === 0 && <span className="muted">nenhum</span>}
        {chapter.rewardItems.map((it, index) => (
          <div key={`${it.contentType}-${it.contentId}-${index}`} className="story-reward-row">
            <span>{it.name || `#${it.contentId}`} × {it.quantity} <span className="muted">({it.contentType === 'weapons' ? 'arma' : 'item do RPG'})</span></span>
            <button
              type="button" className="scene-delete" title="Tirar esta recompensa"
              onClick={() => onSave({ rewardItems: chapter.rewardItems.filter((_, i) => i !== index) })}
            >
              ✕
            </button>
          </div>
        ))}
        <div className="story-reward-row">
          <select className="form-select" value={newReward} onChange={(e) => setNewReward(e.target.value)}>
            <option value="">+ recompensa…</option>
            <optgroup label="Armas (vão para a bolsa pela Comunidade)">
              {rewardPool.weapons.map((w) => <option key={w.id} value={`weapons:${w.id}`}>{w.name}</option>)}
            </optgroup>
            <optgroup label="Itens (bolsa ou ficha, conforme o efeito)">
              {rewardPool.items.map((i) => <option key={i.id} value={`items:${i.id}`}>{i.name}</option>)}
            </optgroup>
          </select>
          <input
            type="number" className="form-input story-qty" min={1} max={99} value={newQty}
            onChange={(e) => setNewQty(Math.max(1, Number(e.target.value)))}
          />
          <button type="button" className="importer-secondary" disabled={!newReward} onClick={addReward}>ok</button>
        </div>
      </fieldset>
    </div>
  );
}

function formatBytes(n: number): string {
  const units = ['B', 'KB', 'MB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export function StoryEditor(): JSX.Element {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chapterId, setChapterId] = useState<number | null>(null);
  const [editingChapterMap, setEditingChapterMap] = useState<Chapter | null>(null);
  const [maps, setMaps] = useState<MapChoice[]>([]);
  const [media, setMedia] = useState<MediaAsset[]>([]);
  const [characters, setCharacters] = useState<Pick[]>([]);
  const [rewardPool, setRewardPool] = useState<{ weapons: Pick[]; items: Pick[] }>({ weapons: [], items: [] });
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const chapter = chapters.find((c) => c.id === chapterId) ?? null;

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await api.get<{ campaigns: Campaign[] }>('/api/historia/campanhas');
      setCampaigns(data.campaigns);
    } catch (e) {
      setError(messageOf(e));
    }
  }, []);

  const loadCampaign = useCallback(async (slug: string) => {
    try {
      const data = await api.get<{ chapters: Chapter[] }>(
        `/api/historia/campanhas/${encodeURIComponent(slug)}`,
      );
      setChapters(data.chapters);
      setOpenSlug(slug);
      setChapterId((current) => (data.chapters.some((c) => c.id === current) ? current : data.chapters[0]?.id ?? null));
    } catch (e) {
      setError(messageOf(e));
    }
  }, []);

  useEffect(() => {
    void loadCampaigns();
    void api.get<{ maps: MapChoice[] }>('/api/maps').then((d) => setMaps(d.maps)).catch(() => undefined);
    void api.get<{ media: MediaAsset[] }>('/api/admin/historia/midia')
      .then((d) => setMedia(d.media)).catch(() => undefined);
    // O que um capítulo pode desbloquear e entregar: o acervo como a partida o vê.
    void api.get<Pick[]>('/api/content/characters').then(setCharacters).catch(() => undefined);
    void Promise.all([api.get<Pick[]>('/api/content/weapons'), api.get<Pick[]>('/api/content/items')])
      .then(([weapons, items]) => setRewardPool({ weapons, items }))
      .catch(() => undefined);
  }, [loadCampaigns]);

  const act = async (run: () => Promise<unknown>, message?: string) => {
    setError('');
    setNotice('');
    try {
      await run();
      if (message) setNotice(message);
      await loadCampaigns();
      if (openSlug) await loadCampaign(openSlug);
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const upload = async (file: File) => {
    setError('');
    setUploading(1);
    try {
      const result = await api.upload<{ media: MediaAsset }>(
        '/api/admin/historia/midia',
        file,
        setUploading,
      );
      setMedia((old) => [result.media, ...old.filter((m) => m.id !== result.media.id)]);
      setNotice(`${file.name} enviado.`);
    } catch (e) {
      setError(messageOf(e, 'O envio falhou.'));
    } finally {
      setUploading(0);
    }
  };

  return (
    <div className="story-editor">
      <header className="story-head">
        <div>
          <h2>📖 Modo História</h2>
          <p className="muted">
            Campanhas com capítulos e cenas — diálogo, narração, imagem e vídeo.
          </p>
        </div>
        <button
          type="button"
          className="importer-submit"
          onClick={() => {
            const title = window.prompt('Nome da nova campanha:');
            if (!title) return;
            void act(
              () => api.post('/api/admin/historia/campanha', { title, status: 'rascunho' }),
              'Campanha criada.',
            );
          }}
        >
          + Nova campanha
        </button>
      </header>

      {error && <p className="importer-error">⚠️ {error}</p>}
      {notice && <p className="importer-notice">✅ {notice}</p>}

      <div className="story-columns">
        {/* ---- campanhas ---- */}
        <aside className="story-col">
          <h3>Campanhas</h3>
          {campaigns.length === 0 && <p className="muted">Nenhuma ainda.</p>}
          <ul className="story-list">
            {campaigns.map((campaign) => (
              <li
                key={campaign.id}
                className={campaign.slug === openSlug ? 'active' : ''}
                onClick={() => void loadCampaign(campaign.slug)}
              >
                <strong>{campaign.title}</strong>
                <span className="muted">
                  {campaign.chapters} capítulo(s) · {campaign.status}
                </span>
              </li>
            ))}
          </ul>

          {openSlug && (
            <div className="story-col-actions">
              {(() => {
                const campaign = campaigns.find((c) => c.slug === openSlug);
                if (!campaign) return null;
                return (
                  <>
                    <label className="form-field">
                      <span>Situação</span>
                      <select
                        className="form-select"
                        value={campaign.status}
                        onChange={(e) => void act(() => api.post('/api/admin/historia/campanha', {
                          id: campaign.id, title: campaign.title, slug: campaign.slug,
                          summary: campaign.summary, status: e.target.value,
                        }), 'Situação alterada.')}
                      >
                        <option value="rascunho">Rascunho (só admins veem)</option>
                        <option value="publicada">Publicada</option>
                        <option value="arquivada">Arquivada</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      className="importer-remove"
                      onClick={() => {
                        if (!window.confirm(`Apagar "${campaign.title}" e todos os capítulos?`)) return;
                        void act(
                          () => api.delete(`/api/admin/historia/campanha/${campaign.id}`),
                          'Campanha apagada.',
                        );
                        setOpenSlug(null);
                        setChapters([]);
                      }}
                    >
                      🗑️ Apagar campanha
                    </button>
                  </>
                );
              })()}
            </div>
          )}
        </aside>

        {/* ---- capítulos ---- */}
        <aside className="story-col">
          <h3>Capítulos</h3>
          {!openSlug ? (
            <p className="muted">Escolha uma campanha.</p>
          ) : (
            <>
              <ul className="story-list">
                {chapters.map((c) => (
                  <li
                    key={c.id}
                    className={c.id === chapterId ? 'active' : ''}
                    onClick={() => setChapterId(c.id)}
                  >
                    <strong>{c.ordinal}. {c.title}</strong>
                    <span className="muted">
                      Mapa #{c.mapId} · ◈ {c.rewardCoins} · {c.scenes.length} cena(s)
                    </span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className="importer-secondary"
                onClick={() => {
                  const title = window.prompt('Nome do capítulo:');
                  if (!title) return;
                  const campaign = campaigns.find((c) => c.slug === openSlug);
                  if (!campaign) return;
                  void act(() => api.post('/api/admin/historia/capitulo', {
                    campaignId: campaign.id, title,
                    mapId: maps[0]?.id ?? 1, rewardCoins: 100, difficulty: 'normal',
                    objective: { kind: 'derrotar_comandante' },
                  }), 'Capítulo criado.');
                }}
              >
                + Novo capítulo
              </button>

              {chapter && (
                <div className="story-col-actions">
                  <label className="form-field">
                    <span>Título</span>
                    <input
                      type="text"
                      className="form-input"
                      defaultValue={chapter.title}
                      onBlur={(e) => {
                        if (e.target.value === chapter.title) return;
                        void act(() => api.post('/api/admin/historia/capitulo', {
                          id: chapter.id, campaignId: campaigns.find((c) => c.slug === openSlug)?.id,
                          title: e.target.value, mapId: chapter.mapId,
                          rewardCoins: chapter.rewardCoins, difficulty: chapter.difficulty,
                        }));
                      }}
                    />
                  </label>
                  <label className="form-field">
                    <span>Mapa</span>
                    <select
                      className="form-select"
                      value={chapter.mapId}
                      onChange={(e) => void act(() => api.post('/api/admin/historia/capitulo', {
                        id: chapter.id, campaignId: campaigns.find((c) => c.slug === openSlug)?.id,
                        title: chapter.title, mapId: Number(e.target.value),
                        rewardCoins: chapter.rewardCoins, difficulty: chapter.difficulty,
                      }))}
                    >
                      {maps.map((m) => (
                        <option key={m.id} value={m.id}>{m.name} ({m.width}×{m.height})</option>
                      ))}
                    </select>
                  </label>
                  <div className="chapter-map-preview-card">
                    <div className="chapter-map-preview-header">
                      <div>
                        <strong>🗺️ Mapa: {maps.find((m) => m.id === chapter.mapId)?.name ?? `Mapa #${chapter.mapId}`}</strong>
                        <span className="chapter-map-dims">
                          {(() => {
                            const found = maps.find((m) => m.id === chapter.mapId);
                            return found ? ` (${found.width}×${found.height})` : '';
                          })()}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn-open-chapter-map"
                        onClick={() => setEditingChapterMap(chapter)}
                        title="Ver e editar este mapa no Editor Tático (com suporte a drag'n'drop de oponentes, baús, teleportes e recrutas)"
                      >
                        🗺️ Ver e Editar Mapa do Capítulo
                      </button>
                    </div>
                    <p className="chapter-map-preview-hint">
                      Posicione e mova (drag'n'drop) oponentes, baús de tesouro, teleportes e unidades recrutáveis deste capítulo.
                    </p>
                  </div>
                  <div className="form-row-2">
                    <label className="form-field">
                      <span>Recompensa (◈)</span>
                      <input
                        type="number"
                        className="form-input"
                        min={0}
                        defaultValue={chapter.rewardCoins}
                        onBlur={(e) => void act(() => api.post('/api/admin/historia/capitulo', {
                          id: chapter.id, campaignId: campaigns.find((c) => c.slug === openSlug)?.id,
                          title: chapter.title, mapId: chapter.mapId,
                          rewardCoins: Number(e.target.value), difficulty: chapter.difficulty,
                        }))}
                      />
                    </label>
                    <label className="form-field">
                      <span>Dificuldade</span>
                      <select
                        className="form-select"
                        value={chapter.difficulty}
                        onChange={(e) => void act(() => api.post('/api/admin/historia/capitulo', {
                          id: chapter.id, campaignId: campaigns.find((c) => c.slug === openSlug)?.id,
                          title: chapter.title, mapId: chapter.mapId,
                          rewardCoins: chapter.rewardCoins, difficulty: e.target.value,
                        }))}
                      >
                        <option value="facil">Fácil</option>
                        <option value="normal">Normal</option>
                        <option value="dificil">Difícil</option>
                      </select>
                    </label>
                  </div>
                  <ChapterRules
                    key={chapter.id}
                    chapter={chapter}
                    characters={characters}
                    rewardPool={rewardPool}
                    onSave={(patch, message) => void act(
                      () => api.post('/api/admin/historia/capitulo', { id: chapter.id, ...patch }),
                      message,
                    )}
                  />
                  <button
                    type="button"
                    className="importer-remove"
                    onClick={() => {
                      if (!window.confirm(`Apagar o capítulo "${chapter.title}"?`)) return;
                      void act(() => api.delete(`/api/admin/historia/capitulo/${chapter.id}`), 'Capítulo apagado.');
                      setChapterId(null);
                    }}
                  >
                    🗑️ Apagar capítulo
                  </button>
                </div>
              )}
            </>
          )}
        </aside>

        {/* ---- cenas ---- */}
        <section className="story-col wide">
          <h3>Cenas {chapter && <span className="muted">de “{chapter.title}”</span>}</h3>

          {!chapter ? (
            <p className="muted">Escolha um capítulo para escrever as cenas.</p>
          ) : (
            <>
              {MOMENTS.map(([moment, label]) => {
                const list = chapter.scenes.filter((s) => s.moment === moment);
                return (
                  <div key={moment} className="scene-group">
                    <header>
                      <h4>{label}</h4>
                      <button
                        type="button"
                        className="importer-secondary"
                        onClick={() => void act(() => api.post('/api/admin/historia/cena', {
                          chapterId: chapter.id, moment, kind: 'dialogo', body: '',
                        }))}
                      >
                        + cena
                      </button>
                    </header>

                    {list.length === 0 ? (
                      <p className="muted">nenhuma cena aqui</p>
                    ) : (
                      list.map((scene) => (
                        <SceneRow
                          key={scene.id}
                          scene={scene}
                          chapterId={chapter.id}
                          media={media}
                          onChange={(patch) => void act(() => api.post('/api/admin/historia/cena', {
                            id: scene.id, chapterId: chapter.id,
                            moment: scene.moment, kind: scene.kind, speaker: scene.speaker,
                            portrait: scene.portrait, portraitIndex: scene.portraitIndex,
                            body: scene.body, background: scene.background,
                            ...patch,
                          }))}
                          onDelete={() => void act(
                            () => api.delete(`/api/admin/historia/cena/${scene.id}`),
                            'Cena apagada.',
                          )}
                        />
                      ))
                    )}
                  </div>
                );
              })}
            </>
          )}
        </section>
      </div>

      {/* ---- biblioteca de mídia ---- */}
      <section className="media-library">
        <header>
          <h3>🎞️ Mídia das cutscenes</h3>
          <label className="media-upload">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,audio/mpeg,audio/ogg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload(file);
                e.target.value = '';
              }}
            />
            {uploading > 0 && uploading < 100 ? `enviando… ${uploading}%` : '⬆️ Enviar imagem ou vídeo'}
          </label>
        </header>

        <p className="muted">
          Imagens até 12 MB, GIF até 20 MB, vídeos (MP4/WebM) até 300 MB, áudio até 50 MB.
          Um arquivo enviado serve a quantas cenas você quiser.
        </p>

        {media.length === 0 ? (
          <p className="muted">Nada enviado ainda.</p>
        ) : (
          <div className="media-grid">
            {media.map((asset) => (
              <figure key={asset.id} className="media-item">
                {asset.kind === 'imagem' ? (
                  <img src={asset.url} alt={asset.originalName} loading="lazy" />
                ) : asset.kind === 'video' ? (
                  <video src={asset.url} muted preload="metadata" />
                ) : (
                  <span className="media-audio">🎵</span>
                )}
                <figcaption title={asset.originalName}>
                  {asset.originalName}
                  <span className="muted">{formatBytes(asset.bytes)}</span>
                </figcaption>
                <button
                  type="button"
                  className="media-delete"
                  title="Apagar do servidor"
                  onClick={() => {
                    if (!window.confirm(`Apagar "${asset.originalName}"?`)) return;
                    void api.delete(`/api/admin/historia/midia/${asset.id}`)
                      .then(() => setMedia((old) => old.filter((m) => m.id !== asset.id)))
                      .catch((e) => setError(messageOf(e)));
                  }}
                >
                  ✕
                </button>
              </figure>
            ))}
          </div>
        )}
      </section>

      {editingChapterMap && (
        <div className="chapter-map-editor-overlay">
          <MapEditor
            initialMapId={editingChapterMap.mapId}
            chapterContext={{
              chapterNumber: editingChapterMap.ordinal,
              chapterTitle: editingChapterMap.title,
              campaignName: campaigns.find((c) => c.slug === openSlug)?.title,
              recruits: editingChapterMap.recruits.map((r) => ({
                characterId: r.characterId ?? 0,
                name: r.unitName,
                recruitmentCondition: r.byName
                  ? `Conversar com ${r.byName}`
                  : 'Conversar com qualquer herói',
              })),
              /*
               * O capítulo passa a jogar a cópia que o editor acabou de gravar.
               *
               * Um mapa do acervo não é regravado: salvar cria uma cópia com id
               * novo. Sem esta linha o autor posicionava os inimigos, via "mapa
               * salvo" e jogava o capítulo contra o mapa original — sem nenhuma
               * das tropas que ele tinha acabado de pôr em campo.
               */
              onMapChanged: (mapId, name) => {
                // O editor de mapas continua aberto no mapa novo: a propriedade
                // `initialMapId` sai daqui.
                setEditingChapterMap((current) => (current ? { ...current, mapId } : current));
                void act(
                  () => api.post('/api/admin/historia/capitulo', {
                    id: editingChapterMap.id,
                    mapId,
                  }),
                  `Capítulo ${editingChapterMap.ordinal} agora joga em "${name}" (#${mapId}).`,
                );
              },
              onClose: () => {
                setEditingChapterMap(null);
                if (openSlug) void loadCampaign(openSlug);
              },
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Uma cena, editável em linha. */
function SceneRow({
  scene,
  media,
  onChange,
  onDelete,
}: {
  readonly scene: Scene;
  readonly chapterId: number;
  readonly media: readonly MediaAsset[];
  readonly onChange: (patch: Record<string, unknown>) => void;
  readonly onDelete: () => void;
}): JSX.Element {
  const needsMedia = scene.kind === 'imagem' || scene.kind === 'video';

  return (
    <article className="scene-row">
      <div className="scene-row-top">
        <select
          className="form-select-sm"
          value={scene.kind}
          onChange={(e) => onChange({ kind: e.target.value })}
        >
          <option value="dialogo">💬 Diálogo</option>
          <option value="narracao">📜 Narração</option>
          <option value="imagem">🖼️ Imagem</option>
          <option value="video">🎬 Vídeo</option>
        </select>

        {scene.kind === 'dialogo' && (
          <>
            <input
              type="text"
              className="form-input-sm"
              placeholder="Quem fala"
              defaultValue={scene.speaker ?? ''}
              onBlur={(e) => onChange({ speaker: e.target.value })}
            />
            <input
              type="text"
              className="form-input-sm"
              placeholder="Retrato (folha do acervo)"
              defaultValue={scene.portrait ?? ''}
              onBlur={(e) => onChange({ portrait: e.target.value })}
            />
            <input
              type="number"
              className="form-input-sm narrow"
              min={0}
              max={7}
              title="Índice do rosto na folha (0 a 7)"
              defaultValue={scene.portraitIndex}
              onBlur={(e) => onChange({ portraitIndex: Number(e.target.value) })}
            />
          </>
        )}

        {needsMedia && (
          <select
            className="form-select-sm"
            defaultValue=""
            onChange={(e) => onChange({ mediaId: Number(e.target.value) || null })}
          >
            <option value="">— escolher mídia —</option>
            {media
              .filter((m) => (scene.kind === 'imagem' ? m.kind === 'imagem' : m.kind === 'video'))
              .map((m) => (
                <option key={m.id} value={m.id}>{m.originalName}</option>
              ))}
          </select>
        )}

        <button type="button" className="scene-delete" onClick={onDelete} title="Apagar esta cena">
          ✕
        </button>
      </div>

      <textarea
        className="form-input scene-text"
        rows={2}
        placeholder={scene.kind === 'narracao' ? 'O texto da narração…' : 'A fala…'}
        defaultValue={scene.body ?? ''}
        onBlur={(e) => onChange({ body: e.target.value })}
      />

      {scene.media && (
        <div className="scene-media-preview">
          {scene.media.kind === 'imagem'
            ? <img src={scene.media.url} alt="" />
            : <video src={scene.media.url} muted preload="metadata" />}
        </div>
      )}
    </article>
  );
}
